import {
    type PriceCalculationUnit,
    type PriceCalculationValueWithUnit,
    type ParameterValueKind,
    type ServiceDefinition,
    type ServiceParameter,
    PriceCalculationParameterInfo,
} from "../api";
import { type BoundTieredValue, type TieredValueTier } from "../api/parameter-values/TieredValue";
import { ResolvedParameterValue } from "./ResolvedParameterValue";

/**
 * Represents a tiered value that applies pricing tiers based on a referenced parameter's value.
 * The tiered value is hidden from customers and only used for pricing calculations.
 */
export class ResolvedTieredValue extends ResolvedParameterValue<undefined, BoundTieredValue> {
    readonly tiers: TieredValueTier[];
    readonly defaultValue: number;
    readonly refParameterId: ServiceParameter.Id;
    readonly unit: PriceCalculationUnit;

    /**
     * Creates a new tiered value.
     *
     * @param value The tiered value data bound to a parameter
     */
    constructor(value: Omit<BoundTieredValue, "kind">) {
        super({
            ...value,
            kind: "tiered",
        });
        this.tiers = value.tiers;
        this.defaultValue = value.defaultValue;
        this.refParameterId = value.refParameterId;
        this.unit = value.unit;
    }

    getKind(): ParameterValueKind {
        return this.kind;
    }

    /**
     * Gets the parameter info for this tiered parameter.
     * Tiered parameters now behave like standard parameters.
     *
     * @returns The parameter info
     */
    getCalculationParameterParameterInfos(): PriceCalculationParameterInfo[] {
        return [
            {
                id: PriceCalculationParameterInfo.Id(this.parameterId.toString()),
                parameterId: this.parameterId,
                unit: this.unit,
                combineWithParameters: this.combineWithParameters,
            },
        ];
    }

    /**
     * Gets the value for the calculation parameter by applying tier mapping.
     * The referenced parameter's value is passed in and mapped through configured tiers.
     *
     * @param refValue The value from the referenced parameter (or undefined)
     * @returns The calculated value after applying tier mapping
     */
    getCalculationParameterValues(
        refValue: number | undefined
    ): Record<string, PriceCalculationValueWithUnit> {
        // If no reference value, use default (no tier lookup)
        if (refValue === undefined) {
            return {
                [this.parameterId.toString()]: {
                    value: this.defaultValue,
                    unit: this.unit,
                },
            };
        }

        // Apply tier mapping to the reference value
        const applicableTiers = this.tiers.filter(t => refValue >= t.from);
        const sortedApplicable = applicableTiers.sort((a, b) => b.from - a.from);
        const tier = sortedApplicable.length > 0 ? sortedApplicable[0] : undefined;
        const calculatedValue = tier?.calculationValue ?? this.defaultValue;

        return {
            [this.parameterId.toString()]: {
                value: calculatedValue,
                unit: this.unit,
            },
        };
    }

    /**
     * Gets the view information for the parameter.
     * Tiered values are hidden from customers, so this returns undefined.
     *
     * @returns undefined (tiered values are not visible to customers)
     */
    getViewText(_value: undefined): string | undefined {
        return undefined;
    }

    /**
     * Creates a customer-safe version of this tiered value.
     * Tiered values are calculation-only and should not be visible to customers.
     *
     * @param _requestPricing - The pricing mode of the service definition (ignored)
     * @returns undefined to indicate this parameter should be hidden from customers
     */
    toCustomerValue(_requestPricing: ServiceDefinition.RequestPricing): undefined {
        return undefined;
    }

    /**
     * Creates a new tiered value with validation.
     *
     * @param value The tiered value data bound to a parameter
     * @returns A new tiered value or throws if validation fails
     */
    static create(value: Omit<BoundTieredValue, "kind">): ResolvedTieredValue | never {
        // Validate tiers exist
        if (!value.tiers || value.tiers.length === 0) {
            throw new Error("TieredValue must have at least one tier");
        }

        // Validate no duplicate 'from' values
        const fromValues = value.tiers.map(t => t.from);
        if (new Set(fromValues).size !== fromValues.length) {
            throw new Error("TieredValue tiers must have unique 'from' values");
        }

        return new ResolvedTieredValue(value);
    }
}
